// timezone-standardization
// eslint-disable-next-line no-restricted-imports
import { format } from 'date-fns';
import { formatInTimeZone, utcToZonedTime } from 'date-fns-tz';
import {
  getTimeZoneName,
  getTimeZoneOffsetMs,
  isLocalTimeZone,
  normalizeIANATimeZone,
  normalizeTimeZone,
} from './time-zone-utils';
import { ONE_DAY, ONE_HOUR, ONE_MINUTE, ONE_SECOND, ONE_YEAR } from './constants';

export const DATE_FORMAT = 'MMM dd, yyyy';
const TIME_FORMATS = {
  '12h': {
    withSeconds: 'hh:mm:ss a',
    withoutSeconds: 'hh:mm a',
    withMs: 'hh:mm:ss.SSS a',
  },
  '24h': {
    withSeconds: 'HH:mm:ss',
    withoutSeconds: 'HH:mm',
    withMs: 'HH:mm:ss.SSS',
  },
};

export type TimeFormat = '12h' | '24h';

type getFormatProps = Pick<GetFormattedDateArgs, 'timeFormat' | 'includeMs' | 'omitSeconds' | 'includeDayOfWeek'>;

/**
 * Helper function to get the correct time format
 */
export const getTimeFormat = ({ timeFormat, includeMs = false, omitSeconds = false }: getFormatProps): string => {
  if (includeMs) {
    return TIME_FORMATS[timeFormat].withMs;
  }
  if (omitSeconds) {
    return TIME_FORMATS[timeFormat].withoutSeconds;
  }
  return TIME_FORMATS[timeFormat].withSeconds;
};

/**
 * Helper function to get the correct date format with optional day of week prefix
 */
export const getDateFormat = ({ includeDayOfWeek = false }: Pick<GetFormattedDateArgs, 'includeDayOfWeek'>): string => {
  return includeDayOfWeek ? `EEE, ${DATE_FORMAT}` : DATE_FORMAT;
};

/**
 * Helper function to get the correct date time format
 */
export const getDateTimeFormat = (options: getFormatProps): string => {
  return `${getDateFormat(options)}, ${getTimeFormat(options)}`;
};

export interface DateRange {
  start: Date;
  end: Date;
  durationMillis?: number;
}

export interface GetFormattedDateArgs {
  /**
   * The date/time value to be formatted, provided as a string or a timestamp.
   */
  date: string | number;
  /**
   * The current user time zone. Must be passed in in order to get the correct date/time.
   */
  timeZone: string | undefined;
  /**
   * Whether to include milliseconds in the time format. (e.g. Jan 01, 2024, 18:00:00.105 CST)
   * @default false
   */
  includeMs?: boolean;
  /**
   * Whether to omit the commas between the day and year, and between the year and the timestamp
   * Can be used in special circumstances when using 'code' typeface
   * (e.g. Returns Jan 01 2024 18:00:00 CST instead of Jan 01, 2024, 18:00:00 CST)
   * @default false
   */
  omitCommas?: boolean;
  /**
   * Whether to omit seconds from the time. By default the time format is hh:mm:ss.
   * @default false
   */
  omitSeconds?: boolean;
  /**
   * Whether or not to include the time zone abbreviation in the time string. (e.g. PST)
   * The time zone is optional when there is already a date time picker on the page (since
   * it already includes the time zone abbreviation)
   * @default false
   */
  omitTimeZoneAbbreviation?: boolean;
  /**
   * Whether to use 12 or 24 hour time. Value comes from the user's selection in the user settings menu.
   * @default '24h'
   */
  timeFormat: TimeFormat;
  /**
   * Whether to include the abbreviated day of week in the date format. (e.g. Mon, Jan 01, 2024, 18:00:00 CST)
   * @default false
   */
  includeDayOfWeek?: boolean;
}

/**
 * Returns a formatted date time string (e.g. Jan 01, 2024, 18:00:00 CST)
 */
export function getFormattedDateTimeString({
  date,
  timeZone,
  includeMs,
  omitCommas,
  omitSeconds,
  omitTimeZoneAbbreviation,
  timeFormat,
  includeDayOfWeek,
}: GetFormattedDateArgs) {
  let dateFormat = getDateTimeFormat({ timeFormat, includeMs, omitSeconds, includeDayOfWeek });
  if (omitCommas) {
    dateFormat = dateFormat.replace(/,/g, '');
  }

  try {
    const timeZoneAbbreviation = !omitTimeZoneAbbreviation ? ` ${getTimeZoneName(timeZone, { variant: 'short' })}` : '';
    return `${formatWithTimeZone(new Date(date), dateFormat, timeZone)}${timeZoneAbbreviation}`;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Failed to format date. `date` or `timeZone` is likely invalid. Error: ', error);
    return '';
  }
}

export type GetFormattedDateStringArgs = Omit<
  GetFormattedDateArgs,
  'includeMs' | 'omitCommas' | 'omitSeconds' | 'omitTimeZoneAbbreviation' | 'timeFormat'
>;

/**
 * Returns a formatted date string (e.g. Jan 01, 2024)
 */
export function getFormattedDateString({ date, timeZone, includeDayOfWeek }: GetFormattedDateStringArgs) {
  try {
    return formatWithTimeZone(new Date(date), getDateFormat({ includeDayOfWeek }), timeZone);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Failed to format date. `date` or `timeZone` is likely invalid. Error: ', error);
    return '';
  }
}

export type GetFormattedTimeArgs = Omit<GetFormattedDateArgs, 'omitCommas'>;

/**
 * Returns a formatted time string (e.g. 18:00:00 CST)
 */
export function getFormattedTimeString({
  date,
  timeZone,
  includeMs,
  omitSeconds,
  omitTimeZoneAbbreviation,
  timeFormat = '24h',
}: GetFormattedTimeArgs) {
  const dateFormat = getTimeFormat({ timeFormat, includeMs, omitSeconds });

  try {
    const timeZoneAbbreviation = !omitTimeZoneAbbreviation ? ` ${getTimeZoneName(timeZone, { variant: 'short' })}` : '';
    return `${formatWithTimeZone(new Date(date), dateFormat, timeZone)}${timeZoneAbbreviation}`;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Failed to format date. `date` or `timeZone` is likely invalid. Error: ', error);
    return '';
  }
}

export interface GetFormattedDateTimeRangeArgs
  extends Pick<GetFormattedDateArgs, 'timeZone' | 'omitSeconds' | 'timeFormat' | 'includeDayOfWeek'> {
  startDate: string;
  endDate: string;
}

/**
 * Returns a formatted date time range (e.g. Jan 01, 2024, 12:00 - Jan 02, 2024, 18:00 CST)
 */
export function getFormattedDateTimeRange({
  startDate,
  endDate,
  timeZone,
  omitSeconds,
  timeFormat,
  includeDayOfWeek,
}: GetFormattedDateTimeRangeArgs) {
  const formattedMinDate = getFormattedDateTimeString({
    date: startDate,
    timeZone,
    omitSeconds,
    omitTimeZoneAbbreviation: true, // omit the time zone from the start date
    timeFormat,
    includeDayOfWeek,
  });
  const formattedMaxDate = getFormattedDateTimeString({
    date: endDate,
    timeZone,
    omitSeconds,
    timeFormat,
    includeDayOfWeek,
  });

  if (formattedMinDate === '' || formattedMaxDate === '') {
    return '';
  }

  return `${formattedMinDate} - ${formattedMaxDate}`;
}

/**
 * Formats the date using the specified format string and time zone. Only use when a custom format is required.
 * Otherwise use one of the appropriate `getFormatted...` utils from the core package.
 */
export function formatWithTimeZone(date: Date, formatString: string, timeZone: string | undefined) {
  const normalizedTimeZone = normalizeTimeZone(timeZone);

  if (isLocalTimeZone(normalizedTimeZone)) {
    return format(date, formatString);
  } else {
    return formatInTimeZone(date, normalizedTimeZone, formatString);
  }
}

function padTime(value: number) {
  return value.toString().padStart(2, '0');
}

type FormatTimeZoneOffsetOpts = {
  /**
   * Variation of the resulting offset formatting.
   * - `long`: Pads the hour with zeros to always be two digits and always includes minutes. (e.g.
   *   UTC, UTC-3:00, UTC+5:30).
   * - `short`: does not pad the hour with zeros and only includes minutes if they are meaningful
   *   (e.g. UTC, UTC-3, UTC+5:30)
   *
   * @default 'long'
   */
  variant?: 'short' | 'long';
};

/**
 * Returns a string that represents the UTC offset for the specified time zone.
 */
export function formatTimeZoneOffset(timeZone?: string, { variant = 'long' }: FormatTimeZoneOffsetOpts = {}) {
  const offsetMs = getTimeZoneOffsetMs(timeZone);

  if (offsetMs === 0) {
    return 'UTC';
  }

  const sign = offsetMs > 0 ? '+' : '-';
  const hours = Math.floor(Math.abs(offsetMs) / ONE_HOUR);
  const minutes = Math.floor((Math.abs(offsetMs) % ONE_HOUR) / ONE_MINUTE);

  let result = 'UTC' + sign;

  if (variant === 'short') {
    // `short` variant does not pad hours with zeros.
    result += hours;
  } else {
    // `long` variant pads hours with zeros.
    result += padTime(hours);
  }

  // Always include minutes when using `long` variant. Only include minutes for
  // `short` variant if they are meaningful (i.e. not zero).
  if (variant !== 'short' || minutes > 0) {
    result += ':' + padTime(minutes);
  }

  return result;
}

export function formatAgo(thenMillis: number, nowMillis: number) {
  const diffMillis = Math.trunc(nowMillis - thenMillis);

  if (diffMillis <= 0) {
    return 'now';
  } else if (diffMillis < ONE_SECOND) {
    return `${diffMillis}ms ago`;
  } else if (diffMillis <= 120 * ONE_SECOND) {
    return `${Math.trunc(diffMillis / ONE_SECOND)}s ago`;
  } else if (diffMillis <= 2 * ONE_HOUR) {
    return `${Math.trunc(diffMillis / ONE_MINUTE)}m ago`;
  } else if (diffMillis <= 2 * ONE_DAY) {
    const hours = Math.trunc(diffMillis / ONE_HOUR);
    return `${hours}h${Math.trunc((diffMillis - hours * ONE_HOUR) / ONE_MINUTE)}m ago`;
  } else if (diffMillis <= 365 * 5 * ONE_DAY) {
    const days = Math.trunc(diffMillis / ONE_DAY);
    return `${days}d${Math.trunc((diffMillis - days * ONE_DAY) / ONE_HOUR)}h ago`;
  } else {
    const years = Math.trunc(diffMillis / ONE_YEAR);
    return `${years}y${Math.trunc((diffMillis - years * ONE_YEAR) / ONE_DAY)}d ago`;
  }
}

/**
 * Returns a compact duration string (e.g. 9h 6m)
 * To get a human readable duration string (e.g. 9 hours 6 minutes), use formatDuration from date-fns.
 */
export function formatDurationCompact(x: number): string {
  if (x < 1.0) {
    return `${parseInt(String(x * 1000), 10)}ms`;
  } else if (x < 60.0) {
    return `${x.toFixed(1)}s`;
  } else if (x < 3600.0) {
    const minutes = Math.floor(x / 60);
    const seconds = Math.floor(x % 60);
    return `${minutes}m ${seconds}s`;
  } else {
    const hours = Math.floor(x / 3600);
    const minutes = Math.floor((x % 3600) / 60);
    return `${hours}h ${minutes}m`;
  }
}

export function formatMicroseconds(microseconds: number) {
  if (microseconds > 1_000_000) {
    return `${(microseconds / 1_000_000).toFixed(2)}s`;
  }

  if (microseconds > 1_000) {
    return `${(microseconds / 1_000).toFixed(2)}ms`;
  }

  return `${microseconds}μs`;
}

export function parseSecondsSinceEpoch(seconds: number, nowFn?: (date?: string) => Date) {
  const millisSinceEpoch = seconds * 1000.0;
  const date = new Date(millisSinceEpoch);
  const isoString = date.toISOString();

  return {
    date,
    agoStr: formatAgo(millisSinceEpoch, (nowFn && nowFn().getTime()) ?? new Date().getTime()),
    isoString,
  };
}

export const convertHoursToDays = (rawHours?: string | null) => {
  if (rawHours === null || rawHours === undefined || rawHours === '') return '';

  const totalHours = Number(rawHours.split('h')[0]);
  const fullDays = Math.trunc(totalHours / 24);
  const remainingHours = totalHours - fullDays * 24;

  if (remainingHours > 0) {
    return `${fullDays}d ${remainingHours}h`;
  }
  return `${fullDays}d`;
};

export function dateTimetoEpoch(dateTimeStr: string) {
  return new Date(dateTimeStr).getTime() / 1000;
}

/**
 * Get the current time adjusted for the selected timezone.
 * If no timezone is set or if the timezone is invalid, returns local time.
 */
export function getNowInTimeZone(timeZone?: string): Date {
  const now = new Date();
  if (!timeZone) return now;

  try {
    return utcToZonedTime(now, normalizeIANATimeZone(timeZone));
  } catch {
    // If the timezone is invalid, fall back to local time
    return now;
  }
}

// Converts a duration (ms) in a human readable format such as ms, s, m, h, d
export function formatMsDuration(durationMs: number): string {
  if (durationMs < 1000) {
    return `${durationMs}ms`;
  }
  if (durationMs < 60 * 1000) {
    return `${(durationMs / 1000).toFixed(0)}s`;
  }
  if (durationMs < 60 * 60 * 1000) {
    return `${(durationMs / (60 * 1000)).toFixed(0)}m`;
  }
  if (durationMs < 24 * 60 * 60 * 1000) {
    return `${(durationMs / (60 * 60 * 1000)).toFixed(0)}h`;
  }
  return `${(durationMs / (24 * 60 * 60 * 1000)).toFixed(0)}d`;
}
